import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Alert,
  Divider,  Grid,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  TextField,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ViewList as ViewListIcon,
  Code as CodeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  useRunJob,
  useArchiveReview,
  useCancelReview,
  useDownloadReview,
} from '../../../services/bulkPricingReview/bulkPricingReview.service';
import { ReviewObjectDto } from '../../../services/bulkPricingReview/bulkPricingReview.types';
import { getAllVariantIdsFromJobRow } from '../utils/bulkReviewJobDisplay';
import { useUser } from '../../../services/auth/auth.service';
import { usePrioritizedVariantInfo } from '../../../services/variants';
import { useFranchiseStores } from '../../../services/patches/patches.service';
import type { PatchLookupMap } from '../../../services/patches/patches.types';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';

interface BulkReviewDetailsProps {
  review: ReviewObjectDto;
  onBack: () => void;
  onRefresh?: () => void;
  onActionCompleted?: () => void;
}

interface InvalidVariantOp {
  variantId: string;

  patchCount: number;
}

const BulkReviewDetails: React.FC<BulkReviewDetailsProps> = ({
  review,
  onBack,
  onRefresh,
  onActionCompleted,
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [fileContent, setFileContent] = useState<string>('');
  const [parsedOperations, setParsedOperations] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'raw' | 'formatted'>('formatted');
  const [loadingContent, setLoadingContent] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50];
  const [filters, setFilters] = useState({
    productName: '',
    storeNumber: '',
  });
  const [showInvalidVariantsDialog, setShowInvalidVariantsDialog] = useState(false);

  const runJobMutation = useRunJob();
  const archiveMutation = useArchiveReview();
  const cancelMutation = useCancelReview();
  const downloadMutation = useDownloadReview();

  const { data: userData } = useUser(true);
  const currentUser = userData?.data;

  const extractFranchiseFromPath = (path: string): string => {
    const parts = path.split('/');
    return parts.length >= 2 ? parts[1] : '';
  };
  const franchiseCode = extractFranchiseFromPath(review.key);
  const fileName = review.key.split('/').pop() || review.key;

  // Reset dialog state when a different review is opened
  useEffect(() => {
    setShowInvalidVariantsDialog(false);
  }, [review.key]);

  // All variant IDs — stable; query key never changes after initial load
  const currentPageVariantIds = useMemo((): string[] => {
    const seen = new Set<string>();
    parsedOperations.forEach(op => {
      getAllVariantIdsFromJobRow(op).forEach(id => seen.add(id));
    });
    return Array.from(seen);
  }, [parsedOperations]);

  // All other variant IDs — loaded in the background for export and filters
  const remainingVariantIds = useMemo((): string[] => {
    const pageSet = new Set(currentPageVariantIds);
    const seen = new Set<string>();
    parsedOperations.forEach(op => {
      getAllVariantIdsFromJobRow(op).forEach(id => {
        if (!pageSet.has(id)) seen.add(id);
      });
    });
    return Array.from(seen);
  }, [parsedOperations, currentPageVariantIds]);

  const currentPageVariantData = usePrioritizedVariantInfo(currentPageVariantIds);
  const backgroundVariantData = usePrioritizedVariantInfo(remainingVariantIds);

  const variantMap = useMemo(() => {
    const merged = new Map(currentPageVariantData.variantMap);
    backgroundVariantData.variantMap.forEach((v, k) => merged.set(k, v));
    return merged;
  }, [currentPageVariantData.variantMap, backgroundVariantData.variantMap]);

  const isLoadingVariants = currentPageVariantData.isLoading;
  const { stores, isLoading: isLoadingStores } = useFranchiseStores(franchiseCode);
  const isLoadingCurrentPageData = currentPageVariantData.isLoading || isLoadingStores;

  const patchLookupMap = useMemo((): PatchLookupMap => {
    const map: PatchLookupMap = {};
    stores.forEach(store => {
      store.assignedMenuDefinitions?.forEach(menuDef => {
        menuDef.patches?.forEach(patch => {
          map[patch.patchId] = {
            storeNumber: store.storeNumber,
            type: 'setprodprices',
            patchName: patch.name,
          };
        });
      });
    });
    return map;
  }, [stores]);

  // True once all variant IDs have been checked (both priority and background queries settled)
  const allVariantsChecked = parsedOperations.length > 0 &&
    (currentPageVariantIds.length === 0 || currentPageVariantData.hasPriorityData) &&
    (remainingVariantIds.length === 0 || backgroundVariantData.hasPriorityData);

  // Raw computation — may transiently return null when page changes reset hasPriorityData
  const computedInvalidVariants = useMemo((): InvalidVariantOp[] | null => {
    if (!allVariantsChecked) return null;
    const seen = new Map<string, InvalidVariantOp>();
    parsedOperations.forEach(op => {
      const seenInThisOp = new Set<string>();
      (op.setOperations ?? []).forEach((setOp: any) => {
        const variantId = setOp?.setVariantPrice?.variantId;
        if (!variantId || variantMap.has(variantId) || seenInThisOp.has(variantId)) return;
        seenInThisOp.add(variantId);
        const patchCount = op.patchIds?.length ?? 0;
        if (!seen.has(variantId)) {
          seen.set(variantId, {
            variantId,

            patchCount,
          });
        } else {
          seen.get(variantId)!.patchCount += patchCount;
        }
      });
    });
    return Array.from(seen.values());
  }, [parsedOperations, variantMap, allVariantsChecked]);

  // Latched: set exactly once per review — pagination re-fires are ignored via lockedRef
  const [invalidVariantOperations, setInvalidVariantOperations] = useState<InvalidVariantOp[] | null>(null);
  const invalidVariantsLockedRef = useRef(false);
  useEffect(() => {
    if (computedInvalidVariants !== null && !invalidVariantsLockedRef.current) {
      invalidVariantsLockedRef.current = true;
      setInvalidVariantOperations(computedInvalidVariants);
    }
  }, [computedInvalidVariants]);
  useEffect(() => {
    invalidVariantsLockedRef.current = false;
    setInvalidVariantOperations(null);
  }, [review.key]);

  const invalidVariantStats = useMemo(() => {
    if (!invalidVariantOperations?.length) return null;
    const invalidIds = new Set(invalidVariantOperations.map(op => op.variantId));
    let affectedRows = 0;
    let droppedRows = 0;
    parsedOperations.forEach(op => {
      const cleanedSetOps = (op.setOperations ?? []).filter((setOp: any) => {
        const id = setOp?.setVariantPrice?.variantId;
        return !id || !invalidIds.has(id);
      });
      const hasInvalid = cleanedSetOps.length < (op.setOperations ?? []).length;
      if (!hasInvalid) return;
      affectedRows++;
      if (cleanedSetOps.length === 0 && (op.removeOperations ?? []).length === 0) droppedRows++;
    });
    return { affectedRows, droppedRows };
  }, [invalidVariantOperations, parsedOperations]);

  useEffect(() => {
    setCurrentPage(1);
  }, [parsedOperations.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [fileContent, fileName]);

  const getVariantDisplayInfo = useCallback((variantId: string) => {
    const variantInfo = variantMap.get(variantId);
    if (!variantInfo) {
      const shortId = variantId.length > 8 ? `...${variantId.slice(-8)}` : variantId;
      return {
        displayName: shortId,
        productName: shortId,
        variantDescription: 'Not found',
        price: 0,
        categories: [],
        options: [],
        isUnknown: true,
      };
    }
    return {
      displayName: variantInfo.fullDescription,
      productName: variantInfo.productName,
      variantDescription: variantInfo.variantDescription,
      price: variantInfo.price,
      categories: variantInfo.categories,
      options: variantInfo.options || [],
      isUnknown: false,
    };
  }, [variantMap]);

  const getPatchDisplayInfo = useCallback((patchIds: string[]) => {
    if (!patchIds || patchIds.length === 0) {
      return { storeNumbers: [], isLoading: false };
    }
    if (!patchLookupMap) {
      return {
        storeNumbers: patchIds.map(id => id.length > 8 ? `...${id.slice(-8)}` : id),
        isLoading: isLoadingCurrentPageData,
      };
    }

    const storeNumbers: string[] = [];
    const unknownPatches: string[] = [];
    patchIds.forEach(patchId => {
      const patchInfo = patchLookupMap[patchId];
      if (patchInfo?.storeNumber) {
        if (!storeNumbers.includes(patchInfo.storeNumber)) {
          storeNumbers.push(patchInfo.storeNumber);
        }
      } else {
        unknownPatches.push(patchId);
      }
    });

    const allDisplayItems = [
      ...storeNumbers,
      ...unknownPatches.map(id => id.length > 8 ? `...${id.slice(-8)}` : id),
    ];

    return {
      storeNumbers: allDisplayItems.length > 0 ? allDisplayItems : [`No stores found for ${patchIds.length} patch(es)`],
      isLoading: isLoadingCurrentPageData,
    };
  }, [patchLookupMap, isLoadingCurrentPageData]);

  const handleRunJob = useCallback(async () => {
    if (!currentUser?.loginId) {
      showToast({ label: 'User not authenticated', id: 'auth-error', toastType: ToastTypes.Error });
      return;
    }
    try {
      await runJobMutation.mutateAsync({ userId: currentUser.loginId, fileName, franchiseCode });
      showToast({ label: 'Job started successfully', id: 'job-start-success', toastType: ToastTypes.Success });
      onRefresh?.();
      onActionCompleted?.();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to start job';
      showToast({ label: errorMessage, id: 'job-start-error', toastType: ToastTypes.Error });
    }
  }, [fileName, franchiseCode, runJobMutation, onRefresh, currentUser]);

  const handleRemoveInvalidAndRun = useCallback(async () => {
    if (!currentUser?.loginId) {
      showToast({ label: 'User not authenticated', id: 'auth-error', toastType: ToastTypes.Error });
      return;
    }
    const invalidIds = new Set(invalidVariantOperations?.map(op => op.variantId) ?? []);
    const cleanedOperations = parsedOperations
      .map(op => ({
        ...op,
        setOperations: (op.setOperations ?? []).filter((setOp: any) => {
          const variantId = setOp?.setVariantPrice?.variantId;
          return !variantId || !invalidIds.has(variantId);
        }),
      }))
      .filter(op => op.setOperations.length > 0 || (op.removeOperations ?? []).length > 0);
    setShowInvalidVariantsDialog(false);
    try {
      await runJobMutation.mutateAsync({
        userId: currentUser.loginId,
        fileName,
        franchiseCode,
        operations: cleanedOperations,
      });
      showToast({ label: 'Job started successfully', id: 'job-start-success', toastType: ToastTypes.Success });
      onRefresh?.();
      onActionCompleted?.();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to start job';
      showToast({ label: errorMessage, id: 'job-start-error', toastType: ToastTypes.Error });
    }
  }, [currentUser, invalidVariantOperations, parsedOperations, fileName, franchiseCode, runJobMutation, onRefresh, onActionCompleted]);

  const handleCancel = useCallback(async () => {
    try {
      if (!currentUser?.loginId) {
        showToast({ label: 'User authentication required to cancel job', id: 'cancel-auth-error', toastType: ToastTypes.Error });
        return;
      }
      console.log('Attempting to cancel job with:', { filePath: review.key });
      await cancelMutation.mutateAsync({ filePath: review.key, userId: currentUser.loginId });
      showToast({ label: 'Job cancelled successfully', id: 'cancel-success', toastType: ToastTypes.Success });
      setShowCancelDialog(false);
      onRefresh?.();
      onActionCompleted?.();
    } catch (error: any) {
      console.error('Cancel job error details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to cancel job';
      showToast({ label: errorMessage, id: 'cancel-error', toastType: ToastTypes.Error });
    }
  }, [fileName, franchiseCode, cancelMutation, onRefresh]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveMutation.mutateAsync({ filePath: review.key });
      showToast({ label: 'Job archived successfully', id: 'archive-success', toastType: ToastTypes.Success });
      onRefresh?.();
      onActionCompleted?.();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to archive job';
      showToast({ label: errorMessage, id: 'archive-error', toastType: ToastTypes.Error });
    }
  }, [fileName, franchiseCode, archiveMutation, onRefresh, onActionCompleted]);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing':
      case 'in_progress': return 'primary';
      case 'pending': return 'info';
      case 'canceled':
      case 'cancelled': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleBack = useCallback(() => { onBack(); }, [onBack]);

  const canRunJob = review.status === 'pending';
  const canCancelJob = review.status === 'pending' || review.status === 'processing';
  const canArchiveJob = review.status === 'completed' || review.status === 'failed' || review.status === 'canceled';

  const hasInvalidVariants = Boolean(invalidVariantOperations && invalidVariantOperations.length > 0);
  const isCheckingVariants = parsedOperations.length > 0 && !allVariantsChecked;

  const invalidIdSet = useMemo(
    () => new Set(invalidVariantOperations?.map(op => op.variantId) ?? []),
    [invalidVariantOperations]
  );

  useEffect(() => {
    const loadContent = async () => {
      setLoadingContent(true);
      try {
        const response = await downloadMutation.mutateAsync({ fileName, franchiseCode });
        let content: string;
        let operations: any[] = [];
        if (typeof response.data === 'string') {
          content = response.data;
          try { operations = JSON.parse(content); } catch { /* not valid JSON */ }
        } else if (response.data && typeof response.data === 'object') {
          content = JSON.stringify(response.data, null, 2);
          operations = Array.isArray(response.data) ? response.data : [response.data];
        } else {
          content = 'No content available';
        }
        setFileContent(content);
        setParsedOperations(Array.isArray(operations) ? operations : []);
      } catch (error: any) {
        console.error('Failed to load file content:', error);
        setFileContent('Failed to load file content. Please try again.');
        setParsedOperations([]);
      } finally {
        setLoadingContent(false);
      }
    };
    loadContent();
  }, [review.key]);

  const filteredOperations = useMemo(() => {
    return parsedOperations.filter(operation => {
      const setOps: any[] = operation.setOperations ?? [];
      const patchDisplay = getPatchDisplayInfo(operation.patchIds || []);

      if (filters.productName) {
        const anyMatch = setOps.some(setOp => {
          const vid = setOp?.setVariantPrice?.variantId;
          if (!vid) return false;
          return getVariantDisplayInfo(vid).productName.toLowerCase().includes(filters.productName.toLowerCase());
        });
        if (!anyMatch) return false;
      }
      if (filters.storeNumber && patchDisplay.storeNumbers.length > 0) {
        const hasMatch = patchDisplay.storeNumbers.some(s => s.toLowerCase().includes(filters.storeNumber.toLowerCase()));
        if (!hasMatch) return false;
      }
      return true;
    });
  }, [parsedOperations, filters, getVariantDisplayInfo, getPatchDisplayInfo]);

  const handleCsvDownload = useCallback(() => {
    const escape = (v: string) => /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const headers = [
      'Operation_Number', 'Franchise_Code', 'Store_Numbers', 'Patch_Count',
      'Variant_ID', 'Product_Name', 'Variant_Description', 'New_Price_USD',
    ];
    let rowNum = 0;
    const rows: string[] = [];
    filteredOperations.forEach(op => {
      const patchDisplay = getPatchDisplayInfo(op.patchIds ?? []);
      const setOps: any[] = op.setOperations ?? [];
      setOps.forEach(setOp => {
        if (!setOp?.setVariantPrice) return;
        rowNum += 1;
        const variantId = setOp.setVariantPrice.variantId ?? '';
        const variantInfo = variantId ? variantMap.get(variantId) : undefined;
        const newPrice = setOp.setVariantPrice.authoringPrice?.amount != null
          ? (setOp.setVariantPrice.authoringPrice.amount / 100).toFixed(2)
          : '';
        rows.push([
          String(rowNum),
          franchiseCode,
          patchDisplay.storeNumbers.join('; '),
          String(op.patchIds?.length ?? 0),
          variantId,
          variantInfo?.productName ?? '',
          variantInfo?.variantDescription ?? '',
          newPrice,
        ].map(escape).join(','));
      });
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.replace(/\.json$/i, '') + '_export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filteredOperations, franchiseCode, fileName, getPatchDisplayInfo, variantMap]);

  const totalUpdateCount = useMemo(
    () => parsedOperations.reduce((sum, op) => sum + ((op.setOperations ?? []).filter((s: any) => s?.setVariantPrice).length), 0),
    [parsedOperations]
  );

  const totalUniqueVariantCount = useMemo(() => {
    const seen = new Set<string>();
    parsedOperations.forEach(op => getAllVariantIdsFromJobRow(op).forEach(id => seen.add(id)));
    return seen.size;
  }, [parsedOperations]);

  const totalFilteredOperations = filteredOperations.length;
  const totalPages = Math.ceil(totalFilteredOperations / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentOperations = filteredOperations.slice(startIndex, endIndex);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="outlined">
          Back to List
        </Button>
        <Typography variant="h4">Review Job Details</Typography>
      </Box>

      {/* Invalid Variants Warning Banner */}
      {invalidVariantOperations && invalidVariantOperations.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
          action={
            <Button color="inherit" size="small" onClick={() => setShowInvalidVariantsDialog(true)}>
              Review
            </Button>
          }
        >
          <strong>{invalidVariantOperations.length} invalid variant ID(s)</strong> detected — these operations will be excluded if you choose "Remove &amp; Run".
        </Alert>
      )}

      {/* Job Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Job Information"
          subheader="Review and manage this bulk pricing job"
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">File Name</Typography>
              <Typography variant="body1" fontWeight="medium">{fileName}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Franchise Code</Typography>
              <Typography variant="body1" fontWeight="medium">{franchiseCode}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip label={review.status} color={getStatusColor(review.status)} size="small" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">File Size</Typography>
              <Typography variant="body1">{formatFileSize(review.size)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Last Modified</Typography>
              <Typography variant="body1">{new Date(review.lastModified).toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">S3 Key</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{review.key}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Operations Analysis Card */}
      {parsedOperations.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Operations Analysis"
            subheader={`${parsedOperations.length} jobs · ${totalUpdateCount} total updates across ${totalUniqueVariantCount} unique variant${totalUniqueVariantCount !== 1 ? 's' : ''}${filteredOperations.length !== parsedOperations.length ? ` (${filteredOperations.length} jobs filtered)` : ''}`}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="formatted" aria-label="formatted view">
                    <ViewListIcon sx={{ mr: 1 }} />
                    Formatted
                  </ToggleButton>
                  <ToggleButton value="raw" aria-label="raw json">
                    <CodeIcon sx={{ mr: 1 }} />
                    Raw JSON
                  </ToggleButton>
                </ToggleButtonGroup>
                {loadingContent ? (
                  <CircularProgress size={24} />
                ) : (
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={viewMode === 'formatted' ? handleCsvDownload : handleDownload}
                    size="small"
                    variant="outlined"
                  >
                    {viewMode === 'formatted' ? 'Download CSV' : 'Download JSON'}
                  </Button>
                )}
              </Box>
            }
          />
          <Divider />
          <CardContent>
            {viewMode === 'formatted' ? (
              <Box>
                {/* Filters */}
                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Filters</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small" fullWidth label="Product Name"
                        value={filters.productName}
                        onChange={(e) => setFilters(prev => ({ ...prev, productName: e.target.value }))}
                        placeholder="Search products..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        size="small" fullWidth label="Store Number"
                        value={filters.storeNumber}
                        onChange={(e) => setFilters(prev => ({ ...prev, storeNumber: e.target.value }))}
                        placeholder="Store number..."
                      />
                    </Grid>
                  </Grid>
                  {(filters.productName || filters.storeNumber) && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setFilters({ productName: '', storeNumber: '' })}>
                        Clear Filters
                      </Button>
                    </Box>
                  )}
                </Card>

                {/* Pagination Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Page Size</InputLabel>
                    <Select
                      value={pageSize}
                      label="Page Size"
                      onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                      {pageSizeOptions.map(size => (
                        <MenuItem key={size} value={size}>{size} per page</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Showing {startIndex + 1}–{Math.min(endIndex, totalFilteredOperations)} of {totalFilteredOperations} operations
                  </Typography>
                  {isLoadingCurrentPageData && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">Loading data...</Typography>
                    </Box>
                  )}
                </Box>

                {/* Operations List */}
                {currentOperations.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {totalFilteredOperations === 0
                      ? 'No operations match the current filters.'
                      : 'No operations on this page. Try adjusting the page size or navigate to a different page.'}
                  </Alert>
                ) : (
                  currentOperations.map((operation, index) => {
                    return (
                      <Accordion key={`accordion-${startIndex}-${index}`} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          {(() => {
                            const patchCount = operation.patchIds?.length ?? 0;
                            const updateCount = (operation.setOperations ?? []).filter((s: any) => s?.setVariantPrice).length;
                            const invalidSetOps = allVariantsChecked
                              ? (operation.setOperations ?? []).filter((s: any) =>
                                  s?.setVariantPrice?.variantId && invalidIdSet.has(s.setVariantPrice.variantId)
                                )
                              : [];
                            const invalidVariantCount = new Set(invalidSetOps.map((s: any) => s.setVariantPrice.variantId)).size;
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography variant="subtitle1">
                                  Job {startIndex + index + 1}
                                </Typography>
                                <Chip
                                  label={`${patchCount} Store${patchCount !== 1 ? 's' : ''}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`${updateCount} update${updateCount !== 1 ? 's' : ''}`}
                                  size="small"
                                  variant="outlined"
                                />
                                {invalidVariantCount > 0 && (
                                  <Chip
                                    label={`${invalidVariantCount} invalid variant${invalidVariantCount !== 1 ? 's' : ''} found — ${patchCount} store${patchCount !== 1 ? 's' : ''} affected`}
                                    size="small"
                                    color="error"
                                  />
                                )}
                              </Box>
                            );
                          })()}
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Store Numbers
                              </Typography>
                              {(() => {
                                const patchDisplay = getPatchDisplayInfo(operation.patchIds || []);
                                if (patchDisplay.isLoading) {
                                  return <Chip label="Loading store numbers..." variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />;
                                }
                                return patchDisplay.storeNumbers.map((storeNumber: string, pIndex: number) => {
                                  let chipColor: 'default' | 'primary' | 'secondary' | 'warning' | 'info' | 'success' | 'error' = 'default';
                                  if (storeNumber.includes('...') || storeNumber.match(/^[a-f0-9]{8}$/)) {
                                    chipColor = 'warning';
                                  } else if (storeNumber.includes('No stores found')) {
                                    chipColor = 'error';
                                  } else if (!isNaN(Number(storeNumber))) {
                                    chipColor = 'primary';
                                  }
                                  return (
                                    <Chip
                                      key={pIndex}
                                      label={storeNumber}
                                      variant="outlined"
                                      size="small"
                                      color={chipColor}
                                      sx={{ mr: 1, mb: 1 }}
                                    />
                                  );
                                });
                              })()}
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Operations
                              </Typography>
                              {(operation.setOperations ?? []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  No set-price operation found.
                                </Typography>
                              ) : (
                                (operation.setOperations ?? []).map((setOp: any, sIdx: number) => {
                                  if (!setOp?.setVariantPrice) return null;
                                  const svVariantId = setOp.setVariantPrice.variantId;
                                  const info = getVariantDisplayInfo(svVariantId);
                                  const newPrice = setOp.setVariantPrice.authoringPrice?.amount;
                                  return (
                                    <Box key={sIdx} sx={{ mt: sIdx > 0 ? 2 : 1, pt: sIdx > 0 ? 2 : 0, borderTop: sIdx > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                      {allVariantsChecked && info.isUnknown ? (
                                        <Alert severity="error" sx={{ mb: 1 }}>
                                          Variant ID <strong>{svVariantId}</strong> was not found in the system and will be excluded from the job request.
                                        </Alert>
                                      ) : !info.isUnknown ? (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Product:</strong> {info.productName}
                                        </Typography>
                                      ) : null}
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Variant ID:</strong> {svVariantId}
                                      </Typography>
                                      {!info.isUnknown && info.options && info.options.length > 0 && (
                                        <Box sx={{ mb: 1 }}>
                                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Options:</strong>
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {info.options.map((option: any, idx: number) => (
                                              <Chip
                                                key={idx}
                                                label={`${option.optionType}: ${option.optionValue}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.75rem' }}
                                              />
                                            ))}
                                          </Box>
                                        </Box>
                                      )}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="body2"><strong>New Price:</strong></Typography>
                                        <Chip
                                          label={`$${((newPrice || 0) / 100).toFixed(2)}`}
                                          size="small"
                                          color="success"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Box>
                                      {!info.isUnknown && info.categories.length > 0 && (
                                        <Box>
                                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Categories:</strong>
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {info.categories.map((category: any, idx: number) => (
                                              <Chip
                                                key={idx}
                                                label={category.name}
                                                size="small"
                                                color="default"
                                                sx={{ fontSize: '0.75rem' }}
                                              />
                                            ))}
                                          </Box>
                                        </Box>
                                      )}
                                      {isLoadingVariants && (
                                        <Typography variant="caption" color="text.secondary">
                                          Loading product details...
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })
                              )}
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(_, page) => setCurrentPage(page)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <TextField
                multiline
                fullWidth
                value={fileContent || ''}
                rows={8}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions Card */}
      <Card>
        <CardHeader
          title="Available Actions"
          subheader="Choose an action to perform on this job"
        />
        <Divider />
        <CardContent>
          {/* Variant validation in-progress indicator */}
          {isCheckingVariants && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
              Validating variant IDs — this may take a moment for large jobs…
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {canRunJob && (
              <Button
                variant="contained"
                color={hasInvalidVariants ? 'warning' : 'primary'}
                startIcon={hasInvalidVariants ? <WarningIcon /> : <PlayIcon />}
                onClick={hasInvalidVariants ? () => setShowInvalidVariantsDialog(true) : handleRunJob}
                disabled={runJobMutation.isLoading || isCheckingVariants}
                size="large"
              >
                {runJobMutation.isLoading
                  ? 'Starting Job...'
                  : isCheckingVariants
                    ? 'Validating...'
                    : hasInvalidVariants
                      ? 'Remove Invalid & Run'
                      : 'Run Job'}
              </Button>
            )}
            {canCancelJob && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelMutation.isLoading}
                size="large"
              >
                Cancel Job
              </Button>
            )}
            {canArchiveJob && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleArchive}
                disabled={archiveMutation.isLoading}
                size="large"
              >
                {archiveMutation.isLoading ? 'Archiving...' : 'Archive Job'}
              </Button>
            )}
          </Box>
          {!canRunJob && !canCancelJob && !canArchiveJob && (
            <Alert severity="info">
              No actions are available for jobs with status "{review.status}".
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Cancel Job</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel this job? This action cannot be undone.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>File: {fileName}</Typography>
          <Typography variant="body2" color="text.secondary">Franchise: {franchiseCode}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>Keep Job</Button>
          <Button onClick={handleCancel} color="error" variant="contained" disabled={cancelMutation.isLoading}>
            {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invalid Variant IDs Dialog */}
      <Dialog
        open={showInvalidVariantsDialog}
        onClose={() => setShowInvalidVariantsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon color="error" />
          Invalid Variant IDs Detected
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{invalidVariantOperations?.length ?? 0} variant ID(s)</strong> were not found in the system — likely deleted or inactive. They appear across <strong>{invalidVariantStats?.affectedRows ?? 0} job row(s)</strong>.{invalidVariantStats?.droppedRows ? ` ${invalidVariantStats.droppedRows} of those row(s) will be dropped entirely (no remaining operations).` : ''}
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following invalid variant IDs will have their set-price entries <strong>stripped from the job request</strong> before submission. All other operations will proceed normally.
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
            {invalidVariantOperations?.map((op, idx) => (
              <Box
                key={idx}
                sx={{ p: 1.5, mb: 1, border: '1px solid', borderColor: 'error.light', borderRadius: 1, backgroundColor: 'error.50' }}
              >
                <Typography variant="body2">
                  <strong>Variant ID:</strong> {op.variantId}
                </Typography>

                <Typography variant="body2">
                  <strong>Affected Patches / Stores:</strong> {op.patchCount}
                </Typography>
              </Box>
            ))}
          </Box>
          <Alert severity="warning">
            Stores associated with the removed operations will <strong>not</strong> receive price updates for those items. All other operations will be processed as normal.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInvalidVariantsDialog(false)} color="inherit">
            Go Back
          </Button>
          <Button
            onClick={handleRemoveInvalidAndRun}
            color="error"
            variant="contained"
            startIcon={<PlayIcon />}
            disabled={runJobMutation.isLoading}
          >
            {runJobMutation.isLoading ? 'Starting Job...' : 'Remove & Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkReviewDetails;
